import { createEmployeeApi, deleteEmployeeApi, getEmployeesApi, updateEmployeeApi } from '@app/api/employee.api';
import { EmployeeDataArray, EmployeeItem, EmployeeQueryParams, EmployeeModel } from '@app/interfaces/employee';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface EmployeeState {
  data: EmployeeModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: EmployeeItem[];
  loading: boolean;
  error: string | null;
}

const initialState: EmployeeState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  // Mặc định chỉ hiện nhân viên đang làm việc (tab lọc nhanh ở EmployeeList).
  filters: { active_eq: true },
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
};

interface ResponseData {
  data: EmployeeItem;
  message: string;
}

export const getEmployees = createAsyncThunk<EmployeeDataArray, EmployeeQueryParams>(
  'employee/getEmployees',
  async (params: EmployeeQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getEmployeesApi(params)) as EmployeeDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createEmployee = createAsyncThunk<ResponseData, EmployeeModel>(
  'employee/createEmployee', async (data) => {
  const response = await createEmployeeApi(data);
  return response;
});

export const updateEmployee = createAsyncThunk<ResponseData, { employeeId: number; formData: EmployeeModel | FormData }>(
  'employee/updateEmployee',
  async ({ employeeId, formData }) => {
    const response = await updateEmployeeApi(employeeId, formData);
    return response;
  },
);

export const deleteEmployee = createAsyncThunk<{ employeeId: number; response: any }, number>(
  'employee/deleteEmployee',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await deleteEmployeeApi(employeeId);
      return { employeeId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: EmployeeItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        const { employeeId, response } = action.payload;
        // deleteEmployeeApi nuốt lỗi (đã tự báo) và trả undefined → không coi là đã xoá.
        if (!response) return;
        state.data = state.data.filter((product) => Number(product.id) !== employeeId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = employeeSlice.actions;

export default employeeSlice.reducer;
